import { Column } from '@repo/ui/data-table'
import React from 'react'

import { IdInput } from '../../lib/types/utils'

// Helper type to check if a type is an array
type IsArray<T> = T extends Array<unknown> ? true : false

// Simpler approach - only go 3 levels deep
type DotNestedKeys<T> = {
  [K in keyof T]-?: NonNullable<T[K]> extends object
    ? IsArray<NonNullable<T[K]>> extends true
      ? `${Extract<K, string>}`
      :
          | `${Extract<K, string>}`
          | {
              [K2 in keyof NonNullable<T[K]>]-?: NonNullable<NonNullable<T[K]>[K2]> extends object
                ? IsArray<NonNullable<NonNullable<T[K]>[K2]>> extends true
                  ? `${Extract<K, string>}.${Extract<K2, string>}`
                  :
                      | `${Extract<K, string>}.${Extract<K2, string>}`
                      | {
                          [K3 in keyof NonNullable<
                            NonNullable<T[K]>[K2]
                          >]-?: `${Extract<K, string>}.${Extract<K2, string>}.${Extract<K3, string>}`
                        }[keyof NonNullable<NonNullable<T[K]>[K2]>]
                : `${Extract<K, string>}.${Extract<K2, string>}`
            }[keyof NonNullable<T[K]>]
    : `${Extract<K, string>}`
}[keyof T]

type NestedKeyOf<T> = DotNestedKeys<T>

type ColumnConfig<T> = {
  order: NestedKeyOf<T>[]
  headers?: Partial<Record<NestedKeyOf<T>, string>>
  customCells?: Partial<Record<NestedKeyOf<T>, (row: IdInput<T>) => React.ReactNode>>
  sortableFields?: NestedKeyOf<T>[]
}

// Generic version of getNestedValue
function getNestedValue<T>(obj: T, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc !== null && typeof acc === 'object' && key in acc) {
      return (acc as Record<string, unknown>)[key]
    }
    return undefined
  }, obj)
}

export function generateColumns<T extends object>(config: ColumnConfig<T>): Column<IdInput<T>>[] {
  const { order, headers = {}, customCells = {}, sortableFields = [] } = config

  return order.map((key) => {
    return {
      accessorKey: key,
      header: (headers as Record<string, string>)[key] ?? key,
      enableSorting: sortableFields.includes(key),
      cell: (customCells as Record<string, (row: IdInput<T>) => React.ReactNode>)[key]
        ? ({ row }) =>
            (customCells as Record<string, (row: IdInput<T>) => React.ReactNode>)[key]?.(
              row.original
            )
        : ({ row }) => getNestedValue(row.original, key)
    }
  })
}
